/**
 * `?view=` — the shareable representation of a module list view.
 *
 * Two forms are accepted, both under the same parameter:
 *
 * 1. **Saved view reference** — a bare UUID (`?view=6f0…`). Resolved through
 *    `/saved-views/:id`, which is org-scoped server-side, so a link from
 *    another organization simply 404s.
 * 2. **Inline state** — base64url of `{ v, o, m, c }` for ad-hoc state that
 *    was never saved. `o` (organization id) and `m` (module type) are carried
 *    inside the payload precisely so a link from org A cannot repaint a table
 *    in org B: the reader compares them before applying anything.
 *
 * `page` and `limit` are never serialized. `createFilterStore` deliberately
 * resets both on rehydrate, and a shared link that pinned someone to page 7 of
 * a list they have never seen would fight that on purpose.
 *
 * @module lib/views/view-url
 */

import type { ModuleTypeEnum, ViewConfig } from '@scrambl/shared'

export const VIEW_URL_PARAM = 'view'

/** Current payload version. Older/newer payloads are ignored, never guessed at. */
const PAYLOAD_VERSION = 1

/**
 * Encoded payloads longer than this are refused — browsers and chat clients
 * truncate very long URLs, and a silently truncated view link is worse than no
 * link. Callers fall back to asking the user to save the view and share that.
 */
const MAX_ENCODED_LENGTH = 6000

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Pagination is per-session, never part of a shared view. */
const VOLATILE_FILTER_KEYS = ['page', 'limit'] as const

export interface ViewUrlPayload {
  /** Payload version */
  v: number
  /** Organization the view was captured in */
  o: string
  /** Module the view belongs to */
  m: ModuleTypeEnum
  /**
   * Saved-view surface (embedded list key). Absent for the module's
   * top-level page, and for every link minted before surfaces existed.
   */
  s?: string
  /** The view configuration itself */
  c: ViewConfig
  /** Optional display name (set when sharing a saved view inline) */
  n?: string
}

export type ParsedViewParam =
  | { kind: 'saved'; id: string }
  | { kind: 'inline'; payload: ViewUrlPayload }

/** Why an otherwise well-formed link was not applied. */
export type ViewUrlRejection =
  | 'organization'
  | 'module'
  | 'surface'
  | 'version'

export function stripVolatileFilters(
  filters: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!filters) return undefined
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(filters)) {
    if ((VOLATILE_FILTER_KEYS as readonly string[]).includes(key)) continue
    if (value === undefined) continue
    next[key] = value
  }
  return Object.keys(next).length > 0 ? next : undefined
}

/** Drop empty sections so short links stay short. */
export function compactViewConfig(config: ViewConfig): ViewConfig {
  const compacted: ViewConfig = {}
  const filters = stripVolatileFilters(config.filters)
  if (filters) compacted.filters = filters
  if (config.columnFilters && Object.keys(config.columnFilters).length > 0) {
    compacted.columnFilters = config.columnFilters
  }
  if (
    config.columnVisibility &&
    Object.keys(config.columnVisibility).length > 0
  ) {
    compacted.columnVisibility = config.columnVisibility
  }
  if (config.columnOrder && Object.keys(config.columnOrder).length > 0) {
    compacted.columnOrder = config.columnOrder
  }
  if (config.columnWidths && Object.keys(config.columnWidths).length > 0) {
    compacted.columnWidths = config.columnWidths
  }
  if (config.groupBy) compacted.groupBy = config.groupBy
  return compacted
}

function toBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(value: string): string | null {
  try {
    const padded = value
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(value.length / 4) * 4, '=')
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    return null
  }
}

/**
 * Serialize a view for the URL. Returns null when there is nothing worth
 * sharing, or when the payload would be too long to survive a paste.
 */
export function encodeViewParam(input: {
  organizationId: string
  moduleType: ModuleTypeEnum
  /** Embedded-list surface; omitted from the payload when null */
  surface?: string | null
  config: ViewConfig
  name?: string
}): string | null {
  const config = compactViewConfig(input.config)
  if (Object.keys(config).length === 0) return null

  const payload: ViewUrlPayload = {
    v: PAYLOAD_VERSION,
    o: input.organizationId,
    m: input.moduleType,
    ...(input.surface ? { s: input.surface } : {}),
    c: config,
    ...(input.name ? { n: input.name } : {}),
  }

  const encoded = toBase64Url(JSON.stringify(payload))
  return encoded.length > MAX_ENCODED_LENGTH ? null : encoded
}

/** Parse a raw `?view=` value. Returns null for anything unrecognizable. */
export function parseViewParam(raw: string | null): ParsedViewParam | null {
  const value = raw?.trim()
  if (!value) return null

  if (UUID_PATTERN.test(value)) {
    return { kind: 'saved', id: value }
  }

  const json = fromBase64Url(value)
  if (!json) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return null
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }

  const candidate = parsed as Partial<ViewUrlPayload>
  if (
    typeof candidate.v !== 'number' ||
    typeof candidate.o !== 'string' ||
    typeof candidate.m !== 'string' ||
    (candidate.s !== undefined && typeof candidate.s !== 'string') ||
    !candidate.c ||
    typeof candidate.c !== 'object' ||
    Array.isArray(candidate.c)
  ) {
    return null
  }

  return { kind: 'inline', payload: candidate as ViewUrlPayload }
}

/**
 * Decide whether an inline payload may be applied here. Returns the config to
 * apply, or the reason it was refused — the caller surfaces that to the user
 * instead of silently doing nothing.
 */
export function resolveInlineView(
  payload: ViewUrlPayload,
  context: {
    organizationId: string | undefined
    moduleType: ModuleTypeEnum
    surface?: string | null
  },
): { config: ViewConfig } | { rejected: ViewUrlRejection } {
  if (payload.v !== PAYLOAD_VERSION) return { rejected: 'version' }
  // Org safety: a link captured in another organization never repaints this
  // one, even though the module and column ids would happily line up.
  if (!context.organizationId || payload.o !== context.organizationId) {
    return { rejected: 'organization' }
  }
  if (payload.m !== context.moduleType) return { rejected: 'module' }
  // Same module, different list: a Client > Projects view carries column ids
  // that the top-level page may not have, and vice versa.
  if ((payload.s ?? null) !== (context.surface ?? null)) {
    return { rejected: 'surface' }
  }

  return { config: compactViewConfig(payload.c) }
}

/** Build a full share URL for the current location. */
export function buildViewShareUrl(href: string, param: string): string {
  const url = new URL(href)
  url.searchParams.set(VIEW_URL_PARAM, param)
  return url.toString()
}
